/**
 * TicketBillingPanel (v2.3.3) — TRAVEL + BILLING on the ticket, for dispatch.
 *
 * Shows the RAW ACTUAL time and the BILLED (rounded) time SIDE BY SIDE, the
 * travel start point (option + pin + explanation) and the road-snapped leg
 * mileage. Distances honour the existing display-unit preference.
 * Also sends the customer their copy of the ticket — which carries ZERO pricing.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { Send as SendIcon, Place as PlaceIcon } from '@mui/icons-material';
import api from '../services/api';
import { fmtDistM } from '../utils/units';

const M_PER_MI = 1609.344;

function fmtMin(m?: number | null) {
  const v = Math.round(Number(m || 0));
  if (!v) return '0 min';
  const h = Math.floor(v / 60);
  const mm = v % 60;
  return h ? `${h}h ${mm}m` : `${mm} min`;
}

const OPTION_LABEL: Record<string, string> = {
  last_job: 'Last job',
  specified: 'Specified place (dropped pin)',
  first_call: 'First call of the day (home/office)',
};

export default function TicketBillingPanel({ ticketId }: { ticketId: number }) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sendMsg, setSendMsg] = useState<{ t: 'success' | 'warning' | 'error'; m: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, c] = await Promise.all([
        api.get(`/tickets/${ticketId}/billing-summary`),
        api.get(`/tickets/${ticketId}/customer-copy`).catch(() => null),
      ]);
      setData(b.data);
      if (c?.data?.to_email) setEmail(c.data.to_email);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => { void load(); }, [load]);

  const sendCopy = async () => {
    setSending(true);
    setSendMsg(null);
    try {
      const r = await api.post(`/tickets/${ticketId}/customer-copy`, {
        to_email: email, save_to_customer: true,
      });
      setSendMsg({
        t: r.data.delivered ? 'success' : 'warning',
        m: r.data.message || r.data.status,
      });
    } catch (e: any) {
      setSendMsg({ t: 'error', m: e?.response?.data?.detail || 'Send failed' });
    } finally {
      setSending(false);
    }
  };

  if (loading) return <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={22} /></Box>;
  if (!data) return <Alert severity="info">No travel/billing data for this ticket yet.</Alert>;

  const legs = data.legs || [];
  const cell = (actual: number, billedV: number) => (
    <>
      <TableCell align="right">
        <Typography variant="body2" color="text.secondary">{fmtMin(actual)}</Typography>
      </TableCell>
      <TableCell align="right">
        <Chip size="small" color="primary" label={fmtMin(billedV)} />
      </TableCell>
    </>
  );

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <Chip
          size="small"
          color={data.call_type === 'emergency' ? 'error' : 'default'}
          label={`${data.call_type} call`}
        />
        <Tooltip title="billed = round up to the interval, then apply the minimum — per leg / per call">
          <Typography variant="caption" color="text.secondary">
            travel: round {data.rules?.travel?.round}m / min {data.rules?.travel?.min}m ·
            {' '}site: round {data.rules?.site?.round}m / min {data.rules?.site?.min}m
          </Typography>
        </Tooltip>
      </Stack>

      <Table size="small" sx={{ mb: 2 }}>
        <TableHead>
          <TableRow>
            <TableCell><strong>Time</strong></TableCell>
            <TableCell align="right"><strong>Actual (raw)</strong></TableCell>
            <TableCell align="right"><strong>Billed</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell>Travel</TableCell>
            {cell(data.travel?.actual_minutes, data.travel?.billed_minutes)}
          </TableRow>
          <TableRow>
            <TableCell>On site</TableCell>
            {cell(data.onsite?.actual_minutes, data.onsite?.billed_minutes)}
          </TableRow>
          <TableRow>
            <TableCell>Mileage (road-snapped)</TableCell>
            <TableCell align="right" colSpan={2}>
              <Typography variant="body2">
                {fmtDistM((data.mileage?.miles || 0) * M_PER_MI)}
              </Typography>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      {legs.length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Travel legs</Typography>
          {legs.map((l: any) => (
            <Box key={l.id} sx={{ p: 1.5, mb: 1, border: '1px solid #e2e8f0', borderRadius: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <PlaceIcon fontSize="small" sx={{ color: '#14548C' }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {OPTION_LABEL[l.start_option] || l.start_option}
                </Typography>
                <Chip size="small" variant="outlined"
                      label={`${Number(l.start_lat).toFixed(5)}, ${Number(l.start_lng).toFixed(5)}`} />
                {l.start_label && <Typography variant="caption" color="text.secondary">{l.start_label}</Typography>}
              </Stack>
              {l.start_explanation && (
                <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                  “{l.start_explanation}”
                </Typography>
              )}
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                started {l.started_at?.replace('T', ' ')} · arrived {l.arrived_at?.replace('T', ' ') || '—'} ·
                {' '}departed {l.departed_at?.replace('T', ' ') || '—'}
              </Typography>
              <Typography variant="caption" display="block" color="text.secondary">
                travel {fmtMin(l.travel_actual_minutes)} actual → <strong>{fmtMin(l.travel_billed_minutes)} billed</strong> ·
                {' '}on site {fmtMin(l.onsite_actual_minutes)} actual → <strong>{fmtMin(l.onsite_billed_minutes)} billed</strong> ·
                {' '}{fmtDistM(Number(l.travel_meters || 0))} ({l.mileage_source})
              </Typography>
            </Box>
          ))}
        </>
      )}

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
        Send the customer a copy of this ticket
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        The customer copy shows the work, the parts (names + quantities) and the times.
        It contains <strong>no pricing of any kind</strong>.
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <TextField
          size="small" fullWidth label="Customer email" value={email}
          onChange={(e) => setEmail(e.target.value)} placeholder="service@customer.com"
        />
        <Button
          variant="contained" startIcon={<SendIcon />} onClick={sendCopy}
          disabled={sending || !email}
        >
          {sending ? 'Sending…' : 'Send copy'}
        </Button>
      </Stack>
      {sendMsg && (
        <Alert severity={sendMsg.t} sx={{ mt: 1.5 }} onClose={() => setSendMsg(null)}>
          {sendMsg.m}
        </Alert>
      )}
    </Box>
  );
}
